import React from 'react'
import { FormattedMessage } from 'react-intl'
import { intl } from 'lang/intl'

import {
  TtButton,
  TtDropdown,
  TtIcon,
  TtMenu,
  TtMenuItem,
  TtPoll,
  TtPollAnswer,
  TtTrayPoll,
} from '@23/react'

import {
  useOverlayState,
  removeOverlay,
} from '../../../../../firebase/hooks/useOverlays'
import usePollsOverlays from '../../../../hooks/usePollsOverlay'
import { type Poll } from '../../models/poll'
import {
  useStartPollMutation,
  useEndPollMutation,
  usePollsQuery,
} from '../../hooks/use-polls'
import { ChyronType } from '../../../../../firebase/model/chyron-model'
import {
  setActiveCard,
  updatePollCard,
  useTrayCard,
  useTrayCardState,
} from '../../../../../firebase/hooks/useTray'
import { type PropsWithTrayItemChildren } from '../../TrayItem'

import { useClosePollConfirmationModal } from './CloseConfirmation/CloseConfirmation'

import './PollCard.scss'

interface PollCardProps {
  id: string
}

const PollCard = ({
  id,
  children,
}: PropsWithTrayItemChildren<PollCardProps>) => {
  const card = useTrayCard(id)
  const cardState = useTrayCardState(id)
  const overlay = useOverlayState()
  const { togglePollOverlay } = usePollsOverlays()
  const { data: polls } = usePollsQuery()

  if (card?.type !== 'poll') return null

  const isChyronShowing =
    overlay?.type === ChyronType.PollResult &&
    overlay.id === card.poll?.id.toString()

  const showPollTvCard = () => {
    if (card.poll) {
      if (!isChyronShowing) {
        togglePollOverlay({
          poll_id: card.poll.id.toString(),
          question: card.poll.question,
          opened_date: card.poll.startedAt,
          open: Number(card.poll.started),
          options: card.poll.options.map((option) => ({
            option: option.option,
            poll_option_id: option.id.toString(),
            answer_percentage: option.count.toString(),
          })),
        })
      }
    }
  }

  const onCardClickHandler = () => {
    setActiveCard(card.id)
    if (card.poll?.startedAt) showPollTvCard()
  }

  const onPollSelectHandler = (pollId: number) => {
    const poll = polls?.find((p) => p.id === pollId)
    if (poll) {
      updatePollCard(card.id, poll)
    }
  }

  return (
    <TtTrayPoll
      subject={intl.formatMessage({ defaultMessage: 'Poll' })}
      selected={cardState === 'active'}
      disabled={cardState === 'done'}
      onTt-click={(event) => {
        if (event.target === event.currentTarget) {
          onCardClickHandler()
        }
      }}
    >
      {polls && polls.length > 0 && !card.poll && (
        <TtDropdown slot="toolbar" placement="bottom-start" closeOnSelect>
          <TtButton
            slot="trigger"
            label={intl.formatMessage({ defaultMessage: 'Select poll' })}
            color="secondary"
            variant="ghost"
            size="x-small"
            circle
          >
            <TtIcon library="one/webinars" name="polls" />
          </TtButton>
          <TtMenu
            onTt-select={(e) =>
              onPollSelectHandler(parseInt(e.detail.item.value, 10))
            }
          >
            {polls?.map((poll) => (
              <TtMenuItem key={poll.id} value={poll.id.toString()}>
                {poll.question}
              </TtMenuItem>
            ))}
          </TtMenu>
        </TtDropdown>
      )}

      {card.poll?.startedAt && (
        <TtButton
          slot="toolbar"
          label={intl.formatMessage({ defaultMessage: 'Hide Poll' })}
          color="secondary"
          variant="ghost"
          size="x-small"
          circle
          onClick={isChyronShowing ? removeOverlay : showPollTvCard}
        >
          <TtIcon
            library="one/webinars"
            name={isChyronShowing ? 'visibility-off' : 'visibility-on'}
          />
        </TtButton>
      )}

      {children?.()}

      {!card.poll ? (
        <FormattedMessage defaultMessage="Select poll to display" />
      ) : (
        <PollResults poll={card.poll} />
      )}
    </TtTrayPoll>
  )
}

// === PollResults === //

interface ResultsProps {
  poll: Poll
}

const PollResults = ({ poll }: ResultsProps) => {
  const closePollConfirmationModal = useClosePollConfirmationModal()
  const { mutate: startPoll, isLoading: isStartLoading } = useStartPollMutation(
    poll.id
  )
  const { mutate: endPoll, isLoading: isEndLoading } = useEndPollMutation(
    poll.id
  )

  return (
    <>
      <TtPoll label={poll.question}>
        {poll.options.map((o) => (
          <TtPollAnswer key={o.id} label={o.option} value={o.count} />
        ))}
      </TtPoll>

      {poll.started ? (
        <TtButton
          color="secondary"
          size="small"
          variant="outline"
          pill
          loading={isEndLoading}
          onClick={async () => {
            const confirmation = await closePollConfirmationModal()
            if (confirmation) endPoll()
          }}
        >
          <FormattedMessage defaultMessage="Close Poll" />
        </TtButton>
      ) : (
        <>
          {poll.startedAt === '' ? (
            <TtButton
              color="secondary"
              size="small"
              pill
              loading={isStartLoading}
              onClick={() => startPoll()}
            >
              <FormattedMessage defaultMessage="Start Poll" />
            </TtButton>
          ) : (
            <TtButton
              color="secondary"
              size="small"
              variant="outline"
              pill
              disabled
            >
              <FormattedMessage defaultMessage="Poll Closed" />
            </TtButton>
          )}
        </>
      )}
    </>
  )
}

export default PollCard
